/**
 * Punto de Atención Form Dialog - Create/Edit
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { PuntoAtencion } from "@/hooks/use-turnos";
import { useApiError } from "@/hooks/use-api-error";
import { useCreatePuntoAtencion, useUpdatePuntoAtencion } from "@/hooks/use-turnos";

interface PuntoAtencionFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  punto: PuntoAtencion | null;
  sucursalId?: number;
  onSuccess: (isNew: boolean) => void;
}

const TIPO_OPTIONS = [
  { value: "RECEPCION", label: "Recepción" },
  { value: "EXTRACCION", label: "Extracción" },
  { value: "CAJA", label: "Caja" },
  { value: "ENTREGA", label: "Entrega" },
];

const puntoSchema = z.object({
  codigo: z.string().min(1, "validation.required"),
  nombre: z.string().min(2, "validation.minLength"),
  tipo: z.enum(["RECEPCION", "EXTRACCION", "CAJA", "ENTREGA"]),
  descripcion: z.string().optional(),
});

type FormData = z.infer<typeof puntoSchema>;

export function PuntoAtencionFormDialog({
  open,
  onOpenChange,
  punto,
  sucursalId,
  onSuccess,
}: PuntoAtencionFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!punto;

  // Mutations
  const createMutation = useCreatePuntoAtencion({
    onSuccess: () => onSuccess(true),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const updateMutation = useUpdatePuntoAtencion({
    onSuccess: () => onSuccess(false),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(puntoSchema),
    defaultValues: {
      codigo: "",
      nombre: "",
      tipo: "RECEPCION",
      descripcion: "",
    },
  });

  // Reset form when dialog opens/closes or punto changes
  useEffect(() => {
    if (open) {
      if (punto) {
        reset({
          codigo: punto.codigo,
          nombre: punto.nombre,
          tipo: punto.tipo,
          descripcion: punto.descripcion || "",
        });
      } else {
        reset({
          codigo: "",
          nombre: "",
          tipo: "RECEPCION",
          descripcion: "",
        });
      }
      setError(null);
    }
  }, [open, punto, reset]);

  const onSubmit = async (data: FormData) => {
    setError(null);

    if (isEditing && punto) {
      updateMutation.mutate({
        id: punto.id,
        data: {
          codigo: data.codigo,
          nombre: data.nombre,
          tipo: data.tipo,
          descripcion: data.descripcion || "",
        },
      });
    } else if (sucursalId) {
      createMutation.mutate({
        codigo: data.codigo,
        nombre: data.nombre,
        tipo: data.tipo,
        descripcion: data.descripcion || "",
        sucursal: sucursalId,
      });
    }
  };

  const tipoValue = watch("tipo");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t("puntosAtencion.editPunto") : t("puntosAtencion.newPunto")}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t("puntosAtencion.editPuntoDesc") : t("puntosAtencion.newPuntoDesc")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          <div className="grid gap-4">
            {/* Código */}
            <div className="space-y-2">
              <Label htmlFor="codigo">{t("puntosAtencion.fields.code")}</Label>
              <Input
                id="codigo"
                placeholder={t("puntosAtencion.fields.codeDesc")}
                {...register("codigo")}
              />
              {errors.codigo && (
                <p className="text-sm text-destructive">{t(errors.codigo.message as string)}</p>
              )}
            </div>

            {/* Nombre */}
            <div className="space-y-2">
              <Label htmlFor="nombre">{t("puntosAtencion.fields.name")}</Label>
              <Input
                id="nombre"
                placeholder={t("puntosAtencion.fields.nameDesc")}
                {...register("nombre")}
              />
              {errors.nombre && (
                <p className="text-sm text-destructive">{t(errors.nombre.message as string)}</p>
              )}
            </div>

            {/* Tipo */}
            <div className="space-y-2">
              <Label htmlFor="tipo">{t("puntosAtencion.fields.type")}</Label>
              <Select
                value={tipoValue}
                onValueChange={(value) => setValue("tipo", value as FormData["tipo"])}
              >
                <SelectTrigger id="tipo" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPO_OPTIONS.map((tipo) => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Descripción */}
            <div className="space-y-2">
              <Label htmlFor="descripcion">{t("puntosAtencion.fields.description")}</Label>
              <Textarea
                id="descripcion"
                placeholder={t("puntosAtencion.fields.descriptionPlaceholder")}
                {...register("descripcion")}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? t("common.save") : t("common.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
